import React, { useCallback, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { db } from './firebase';
import { collection, writeBatch, doc, getDocs, deleteDoc } from 'firebase/firestore';

const CLIENTS = [
  { id: '2ejjeh', label: '2ejjeh' },
  { id: 'lucea', label: 'lucea' }
];

function BulkImportPage({ userRole, userClientId }) {
  // Debug logging
  console.log('BulkImportPage - userRole:', userRole);
  console.log('BulkImportPage - userClientId:', userClientId);
  console.log('BulkImportPage - userRole === "admin":', userRole === 'admin');
  
  const [selectedClient, setSelectedClient] = useState(userClientId || '2ejjeh');
  const [files, setFiles] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [summary, setSummary] = useState(null);
  const [batchSize, setBatchSize] = useState(200); // keep well under 500 limit
  const [throttleMs, setThrottleMs] = useState(250); // delay between batch commits
  const [isDeleting, setIsDeleting] = useState(false);

  const inputRef = useRef(null);

  const acceptTypes = useMemo(() => '.xls,.xlsx', []);

  const onFolderPick = useCallback((event) => {
    const fileList = Array.from(event.target.files || []);
    const onlyExcel = fileList.filter(f => /\.xlsx?$/.test(f.name));
    setFiles(onlyExcel);
    setSummary(null);
    setErrorText('');
  }, []);

  const parseExcelFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
    return rows;
  };

  const normalizeDateFromFilename = (filename) => {
    const base = filename.replace(/\.(xlsx|xls)$/i, '');
    // Try common patterns: YYYY-MM-DD, DD-MM-YYYY, YYYYMMDD
    const iso = base
      .replace(/_/g, '-')
      .replace(/\./g, '-')
      .trim();
    // Fallback to base if cannot parse cleanly
    return iso;
  };

  const handleImport = useCallback(async () => {
    if (!files.length) return;
    setIsImporting(true);
    setProgressText('Starting import...');
    setErrorText('');

    let totalFiles = 0;
    let totalRows = 0;
    let failedFiles = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgressText(`Parsing ${file.name} (${i + 1}/${files.length})...`);

        try {
          const rows = await parseExcelFile(file);
          const dateKey = normalizeDateFromFilename(file.name);

          // collection path: clients/{clientId}/imports/{date}/items
          const itemsCollection = collection(db, 'clients', selectedClient, 'imports', dateKey, 'items');
          const importDocRef = doc(db, 'clients', selectedClient, 'imports', dateKey);

          // Firestore batch with chunking and throttling
          let batch = writeBatch(db);
          let opsInBatch = 0;
          let writtenForFile = 0;
          const usedWaybills = new Set(); // Track used waybill IDs to avoid duplicates

          // Normalize headers and pick only required columns
          const headerAliases = {
            recipientName: ["Recipient Name", "Recipient name", "Name"],
            recipientNumber: ["Recipient Number", "Recipient number", "Phone", "Number"],
            area: ["Area"],
            directions: ["Directions", "Address", "Location"],
            cashCollection: ["Cash Collection", "COD", "Cash"],
            packageQuantity: ["Package Quantity", "Qty", "Quantity"],
            waybill: ["Waybill #", "Waybill", "Tracking", "Tracking #"],
            notes: ["Notes", "Note", "Remarks"]
          };

          const getCell = (row, names) => {
            for (let i = 0; i < names.length; i++) {
              const key = names[i];
              if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
              // case-insensitive match
              const found = Object.keys(row).find(k => k.toLowerCase().trim() === String(key).toLowerCase().trim());
              if (found) return row[found];
            }
            return '';
          };

          const toNumber = (val) => {
            if (typeof val === 'number') return val;
            const n = Number(String(val).toString().replace(/[^0-9.\-]/g, ''));
            return Number.isFinite(n) ? n : 0;
          };

          // Create the import document with metadata
          const importDocData = {
            createdAt: new Date(),
            date: dateKey,
            totalOrders: 0 // Will be updated after processing all rows
          };
          batch.set(importDocRef, importDocData);
          opsInBatch++;

          for (let r = 0; r < rows.length; r++) {
            const raw = rows[r];
            const area = String(getCell(raw, headerAliases.area) || '').trim();
            
            // Skip rows where area is null or empty
            if (!area) {
              continue;
            }
            
            const data = {
              recipientName: String(getCell(raw, headerAliases.recipientName) || '').trim(),
              recipientNumber: String(getCell(raw, headerAliases.recipientNumber) || '').trim(),
              area: area,
              directions: String(getCell(raw, headerAliases.directions) || '').trim(),
              cashCollection: toNumber(getCell(raw, headerAliases.cashCollection)),
              packageQuantity: toNumber(getCell(raw, headerAliases.packageQuantity)),
              waybill: String(getCell(raw, headerAliases.waybill) || '').trim(),
              notes: String(getCell(raw, headerAliases.notes) || '').trim(),
              clientId: selectedClient,
              importDateKey: dateKey
            };

            // Generate unique ID - use waybill if available and not already used, otherwise use random number
            let docId;
            const waybill = data.waybill && String(data.waybill).trim();
            
            if (waybill && !usedWaybills.has(waybill)) {
              docId = `${dateKey}_${waybill}`;
              usedWaybills.add(waybill);
            } else {
              // Use random number for duplicates or missing waybills
              docId = `${dateKey}_${Math.random().toString(36).substr(2, 9)}`;
            }
            
            const newDoc = doc(itemsCollection, docId);
            batch.set(newDoc, data);
            opsInBatch++;
            writtenForFile++;
            totalRows++;

            if (opsInBatch >= batchSize) {
              await batch.commit();
              // throttle to avoid RESOURCE_EXHAUSTED
              if (throttleMs > 0) {
                await new Promise(res => setTimeout(res, throttleMs));
              }
              batch = writeBatch(db);
              opsInBatch = 0;
            }
          }

          // Update the import document with the final totalOrders count
          if (writtenForFile > 0) {
            batch.update(importDocRef, { totalOrders: writtenForFile });
            opsInBatch++;
          }

          if (opsInBatch > 0) {
            await batch.commit();
            if (throttleMs > 0) {
              await new Promise(res => setTimeout(res, throttleMs));
            }
          }

          totalFiles++;
          setProgressText(`Imported ${writtenForFile} rows from ${file.name}`);
        } catch (fileErr) {
          console.error(fileErr);
          failedFiles.push(file.name);
          setErrorText(prev => prev ? prev + `\n${file.name}: ` + (fileErr?.message || 'unknown error') : `${file.name}: ` + (fileErr?.message || 'unknown error'));
        }
      }

      setSummary({ totalFiles, totalRows, failedFiles });
      setProgressText('Import finished.');
    } catch (e) {
      console.error(e);
      setErrorText(e?.message || 'Unexpected error during import');
    } finally {
      setIsImporting(false);
    }
  }, [files, selectedClient]);

  const handleDeleteAll = useCallback(async () => {
    if (!window.confirm(`Are you sure you want to delete ALL data for client "${selectedClient}"? This cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    setProgressText('Deleting all data...');
    setErrorText('');

    try {
      // Get all imports for this client
      const importsCollection = collection(db, 'clients', selectedClient, 'imports');
      const importsSnapshot = await getDocs(importsCollection);
      
      let totalDeleted = 0;
      let batch = writeBatch(db);
      let opsInBatch = 0;

      for (const importDoc of importsSnapshot.docs) {
        const itemsCollection = collection(db, 'clients', selectedClient, 'imports', importDoc.id, 'items');
        const itemsSnapshot = await getDocs(itemsCollection);
        
        for (const itemDoc of itemsSnapshot.docs) {
          batch.delete(itemDoc.ref);
          opsInBatch++;
          totalDeleted++;

          if (opsInBatch >= batchSize) {
            await batch.commit();
            if (throttleMs > 0) {
              await new Promise(res => setTimeout(res, throttleMs));
            }
            batch = writeBatch(db);
            opsInBatch = 0;
          }
        }

        // Delete the import document itself
        batch.delete(importDoc.ref);
        opsInBatch++;

        if (opsInBatch >= batchSize) {
          await batch.commit();
          if (throttleMs > 0) {
            await new Promise(res => setTimeout(res, throttleMs));
          }
          batch = writeBatch(db);
          opsInBatch = 0;
        }
      }

      if (opsInBatch > 0) {
        await batch.commit();
      }

      setProgressText(`Deleted ${totalDeleted} items and ${importsSnapshot.docs.length} import records.`);
      setSummary(null);
    } catch (e) {
      console.error(e);
      setErrorText(e?.message || 'Failed to delete data');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedClient, batchSize, throttleMs]);

  return (
    <div style={{ 
      padding: '32px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#007bff',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '16px'
          }}>
            <span style={{ color: 'white', fontSize: '24px' }}>📊</span>
          </div>
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '28px', 
              color: '#2c3e50',
              fontWeight: '600'
            }}>
              Bulk Import Excel Files
            </h1>
            <p style={{ 
              margin: '4px 0 0 0', 
              color: '#6c757d', 
              fontSize: '16px' 
            }}>
              Upload and process Excel files to import data into the system
            </p>
          </div>
        </div>

        {/* Client Selection */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: '#495057',
            fontWeight: '600',
            fontSize: '16px'
          }}>
            Target Client
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <select
              id="client"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              disabled={isImporting || userRole !== 'admin'}
              style={{
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: userRole !== 'admin' ? '#f8f9fa' : 'white',
                color: '#495057',
                minWidth: '200px',
                cursor: userRole !== 'admin' ? 'not-allowed' : 'pointer'
              }}
            >
              {CLIENTS.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            {userRole !== 'admin' && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#e3f2fd',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#1976d2',
                border: '1px solid #bbdefb'
              }}>
                🔒 Restricted to: {userClientId}
              </div>
            )}
          </div>
        </div>

        {/* Advanced Settings */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            color: '#495057',
            fontWeight: '600'
          }}>
            ⚙️ Advanced Settings
          </h3>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#6c757d',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Batch Size
              </label>
              <input
                type="number"
                min={50}
                max={450}
                step={50}
                value={batchSize}
                onChange={(e) => setBatchSize(Math.max(50, Math.min(450, Number(e.target.value) || 200)))}
                disabled={isImporting}
                style={{
                  width: '120px',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                Records per batch
              </div>
            </div>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#6c757d',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Throttle Delay
              </label>
              <input
                type="number"
                min={0}
                step={50}
                value={throttleMs}
                onChange={(e) => setThrottleMs(Math.max(0, Number(e.target.value) || 0))}
                disabled={isImporting}
                style={{
                  width: '120px',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                Milliseconds between batches
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File Upload Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          margin: '0 0 24px 0', 
          fontSize: '22px', 
          color: '#2c3e50',
          fontWeight: '600'
        }}>
          📁 File Upload
        </h2>

        {/* Folder Upload */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            marginBottom: '12px',
            color: '#495057',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            Upload Multiple Files (Folder)
          </label>
          <div style={{
            border: '2px dashed #007bff',
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
            backgroundColor: '#f8f9ff',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onClick={() => inputRef.current?.click()}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
            <div style={{ fontSize: '18px', color: '#007bff', marginBottom: '8px', fontWeight: '600' }}>
              Click to select a folder
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d' }}>
              Select a folder containing Excel files (.xls, .xlsx)
            </div>
            <input
              ref={inputRef}
              type="file"
              accept={acceptTypes}
              webkitdirectory="true"
              directory="true"
              multiple
              onChange={onFolderPick}
              disabled={isImporting}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {/* Single File Upload */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            marginBottom: '12px',
            color: '#495057',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            Upload Single File
          </label>
          <div style={{
            border: '2px dashed #28a745',
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
            backgroundColor: '#f8fff8',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.xls,.xlsx';
            input.onchange = (e) => {
              const f = e.target.files && e.target.files[0];
              setFiles(f ? [f] : []);
              setSummary(null);
              setErrorText('');
            };
            input.click();
          }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
            <div style={{ fontSize: '18px', color: '#28a745', marginBottom: '8px', fontWeight: '600' }}>
              Click to select a single file
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d' }}>
              Select a single Excel file (.xls, .xlsx)
            </div>
          </div>
        </div>

        {/* File Status */}
        {files.length > 0 && (
          <div style={{
            backgroundColor: '#e8f5e8',
            border: '1px solid #c3e6c3',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>✅</span>
              <span style={{ fontWeight: '600', color: '#155724' }}>
                {files.length} file(s) selected
              </span>
            </div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#155724' }}>
              {files.map((file, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  • {file.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={handleImport} 
            disabled={!files.length || isImporting || isDeleting}
            style={{
              backgroundColor: files.length && !isImporting && !isDeleting ? '#007bff' : '#6c757d',
              color: 'white',
              border: 'none',
              padding: '14px 28px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: files.length && !isImporting && !isDeleting ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
          >
            {isImporting ? '⏳' : '🚀'} {isImporting ? 'Importing...' : 'Start Import'}
          </button>
        </div>
      </div>

      {/* Progress Section */}
      {progressText && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            color: '#495057',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ⏳ Progress
          </h3>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#495057',
            whiteSpace: 'pre-wrap',
            border: '1px solid #e9ecef'
          }}>
            {progressText}
          </div>
        </div>
      )}

      {/* Error Section */}
      {errorText && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #f5c6cb'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            color: '#721c24',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ❌ Error
          </h3>
          <div style={{
            backgroundColor: '#f8d7da',
            padding: '16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#721c24',
            whiteSpace: 'pre-wrap',
            border: '1px solid #f5c6cb'
          }}>
            {errorText}
          </div>
        </div>
      )}

      {/* Summary Section */}
      {summary && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #c3e6cb'
        }}>
          <h3 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px', 
            color: '#155724',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ✅ Import Summary
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724' }}>
                {summary.totalFiles}
              </div>
              <div style={{ fontSize: '14px', color: '#155724' }}>
                Files Imported
              </div>
            </div>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724' }}>
                {summary.totalRows}
              </div>
              <div style={{ fontSize: '14px', color: '#155724' }}>
                Rows Processed
              </div>
            </div>
            {summary.failedFiles?.length > 0 && (
              <div style={{
                backgroundColor: '#f8d7da',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
                gridColumn: '1 / -1'
              }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#721c24', marginBottom: '8px' }}>
                  Failed Files
                </div>
                <div style={{ fontSize: '14px', color: '#721c24' }}>
                  {summary.failedFiles.join(', ')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkImportPage;


